import ACTION_TYPES from '@/Common/Enum/ActionType.js';
import { API_STATUS } from '@/Common/Enum/ApiStatus.js';
import ActionType from '@/Common/Enum/ActionType.js';
import i18n from '@/Plugins/i18n.js';

const t = (key) => i18n.global.t(`common.dns_record_status.${key}`);

export function getDmarcDnsRecordStatus(result) {
  if (!result.impersonation.has_dmarc_record) {
    return {
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      message: t('record_not_found')
    };
  }

  const hasTooManyRecords = (result.records?.dmarc?.tags || []).some(
    (tag) => tag.msg === 'Too many records'
  );

  if (hasTooManyRecords) {
    return {
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      message: t('multiple_dmarc_records_found')
    };
  }

  if (
    [API_STATUS.FAIL, API_STATUS.FATAL].includes(result.records?.dmarc?.status)
  ) {
    return {
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      message: t('invalid_record_found')
    };
  }

  if (result.records?.dmarc?.status === API_STATUS.ERROR) {
    return {
      actionType: ACTION_TYPES.ACTION_RECOMMENDED,
      message: t('record_found_with_errors')
    };
  }

  if (result.records?.dmarc?.status === API_STATUS.INFO) {
    return {
      actionType: ACTION_TYPES.INFO,
      message: t('record_found_with_warnings')
    };
  }

  return {
    actionType: ACTION_TYPES.NO_ACTION,
    message: t('record_found_without_errors')
  };
}

export function getSpfDnsRecordStatus(result) {
  const tags = Object.fromEntries(
    result.records?.spf?.tags?.map((tag) => [tag.msg, tag]) ?? []
  );

  if (tags['no spf no mx'] && !result.impersonation?.has_spf_record) {
    return {
      actionType: ACTION_TYPES.INFO,
      message: t('no_mx_records_spf_not_required'),
      canAnalyse: false
    };
  }

  if (!result.impersonation?.has_spf_record) {
    return {
      actionType: ACTION_TYPES.NO_ACTION,
      message: t('record_not_found'),
      canAnalyse: false
    };
  }

  if (tags['Too many records']) {
    return {
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      message: t('multiple_spf_records_found'),
      canAnalyse: true
    };
  }

  const statusMap = {
    [API_STATUS.FAIL]: {
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      message: t('invalid_record_found'),
      canAnalyse: true
    },
    [API_STATUS.FATAL]: {
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      message: t('invalid_record_found'),
      canAnalyse: true
    },
    [API_STATUS.ERROR]: {
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      message: t('record_found_with_errors'),
      canAnalyse: true
    },
    [API_STATUS.INFO]: {
      actionType: ACTION_TYPES.ACTION_RECOMMENDED,
      message: t('record_found_with_warnings'),
      canAnalyse: true
    }
  };

  return (
    statusMap[result.records?.spf?.status] ?? {
      actionType: ACTION_TYPES.NO_ACTION,
      message: t('record_found_without_errors'),
      canAnalyse: true
    }
  );
}

export function getDkimDnsRecordStats(result) {
  const tags = Object.fromEntries(
    result.records?.dkim?.tags?.map((tag) => [tag.msg, tag]) ?? []
  );

  const tagMessages = {
    'weak keys': {
      message: t('weak_dkim_keys_found'),
      actionType: ActionType.ACTION_RECOMMENDED
    },
    'invalid keys': {
      message: t('invalid_dkim_keys_found'),
      actionType: ActionType.ACTION_RECOMMENDED
    },
    'no keys': {
      message: t('no_known_dkim_keys_found'),
      actionType: ActionType.INFO
    },
    'error keys': {
      message: t('dkim_keys_contain_errors'),
      actionType: ActionType.ACTION_RECOMMENDED
    },
    'no dkim no mx': {
      message: t('no_mx_records_dkim_not_required'),
      actionType: ActionType.INFO
    },
    'large keys': {
      message: t('large_dkim_keys_found'),
      actionType: ActionType.INFO
    },
    'no errors': {
      message: t('dkim_keys_found_without_errors'),
      actionType: ActionType.NO_ACTION
    },
    'dkim wildcard': {
      message: t('dkim_lookup_contains_wildcard'),
      actionType: ActionType.ACTION_RECOMMENDED
    }
  };

  let actionType = null;
  let messages = [];

  Object.keys(tags).forEach((tag) => {
    if (tagMessages.hasOwnProperty(tag)) {
      if (actionType === null) {
        actionType = tagMessages[tag].actionType;
      }
      messages.push(tagMessages[tag].message);
    }
  });

  if (
    tags.hasOwnProperty('no dkim no mx') &&
    !result.impersonation.num_dkim_records
  ) {
    return {
      actionType,
      message: t('no_mx_records_dkim_not_required'),
      canAnalyse: false
    };
  }

  if (!result.impersonation.num_dkim_records) {
    return {
      actionType: ACTION_TYPES.INFO,
      message: t('no_known_dkim_keys_found'),
      canAnalyse: false
    };
  }

  if (!actionType) {
    return {
      actionType: ActionType.NO_ACTION,
      message: t('dkim_keys_found_without_errors'),
      canAnalyse: true
    };
  }

  return { actionType, message: messages.join('. '), canAnalyse: true };
}

export function getBimiDnsRecordStatus(result) {
  if (!result.marketing.has_bimi_record) {
    return {
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      message: t('no_record_found'),
      canAnalyse: false
    };
  }

  if (result.records.bimi.tags.some((tag) => tag.msg === 'Invalid record')) {
    return {
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      message: t('invalid_record_found'),
      canAnalyse: true
    };
  }

  if (result.records.bimi.status === API_STATUS.ERROR) {
    return {
      actionType: ACTION_TYPES.ACTION_RECOMMENDED,
      message: t('record_contains_errors'),
      canAnalyse: true
    };
  }

  return {
    actionType: ACTION_TYPES.NO_ACTION,
    message: t('record_found_without_errors'),
    canAnalyse: true
  };
}

export function getTlsDnsRecordStatus(result) {
  if (!result.privacy.has_tlsrpt_record) {
    return {
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      message: t('no_record_found'),
      canAnalyse: false
    };
  }

  const tags = Object.fromEntries(
    result.records?.tlsrpt?.tags?.map((tag) => [tag.msg, tag]) ?? []
  );

  if (tags['Too many records']) {
    return {
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      message: t('multiple_records_found'),
      canAnalyse: true
    };
  }

  if (
    [API_STATUS.FAIL, API_STATUS.FATAL].includes(result.records?.tlsrpt?.status)
  ) {
    return {
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      message: t('invalid_record_found'),
      canAnalyse: true
    };
  }

  if (tags['errors']) {
    return {
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      message: t('record_contains_errors'),
      canAnalyse: true
    };
  }

  return {
    actionType: ACTION_TYPES.NO_ACTION,
    message: t('record_found_without_errors'),
    canAnalyse: true
  };
}

export function getMtaDnsRecordStatus(result) {
  if (!result.privacy.has_mtasts_record) {
    return {
      message: t('no_record_found'),
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      canAnalyse: false
    };
  }

  const tags = Object.fromEntries(
    result.records?.mtasts?.tags?.map((tag) => [tag.msg, tag]) ?? []
  );

  if (tags.hasOwnProperty('Too many records')) {
    return {
      message: t('multiple_records_found'),
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      canAnalyse: true
    };
  }

  if (
    [API_STATUS.FAIL, API_STATUS.FATAL].includes(result.records?.mtasts?.status)
  ) {
    return {
      message: t('invalid_record_found'),
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      canAnalyse: true
    };
  }

  if (tags.hasOwnProperty('errors')) {
    return {
      message: t('record_contain_errors'),
      actionType: ACTION_TYPES.ACTION_REQUIRED,
      canAnalyse: true
    };
  }

  return {
    message: t('record_found_without_errors'),
    actionType: ACTION_TYPES.NO_ACTION,
    canAnalyse: true
  };
}
